import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PageTransition from '@/components/ui/PageTransition';
import { useToast } from '@/hooks/use-toast';
import { Auth as FirebaseAuth } from '@/integrations/firebase/config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import EnhancedPersonalInfoForm, { 
  EnhancedPersonalInfoFormValues 
} from '@/components/forms/EnhancedPersonalInfoForm';
import { biodataService } from '@/services/biodataService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const Profile = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Get current user
  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['authUser'],
    queryFn: async () => {
      return FirebaseAuth.currentUser;
    }
  });

  // Get existing profile data
  const { data: profileData, isLoading: profileLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => biodataService.getProfile(),
    enabled: !!user,
  });

  // Save profile mutation
  const saveProfileMutation = useMutation({
    mutationFn: async ({ formData, privacySettings }: { 
      formData: EnhancedPersonalInfoFormValues, 
      privacySettings: Record<string, boolean> 
    }) => {
      const profileData = {
        fullName: formData.fullName,
        gender: formData.gender,
        dateOfBirth: formData.dateOfBirth,
        heightCm: formData.heightCm ? parseInt(formData.heightCm) : undefined,
        weightKg: formData.weightKg ? parseInt(formData.weightKg) : undefined,
        displayEmail: formData.displayEmail,
        residenceCity: formData.residenceCity,
        residenceCountry: formData.residenceCountry,
        religion: formData.religion,
        casteOrSect: formData.casteOrSect,
        motherTongue: formData.motherTongue,
        aboutMe: formData.aboutMe,
        privateFields: {
          biodata: privacySettings,
        },
      };
      
      return await biodataService.saveProfile(profileData);
    },
    onSuccess: () => {
      toast({
        title: 'Profile saved successfully',
        description: 'Your profile and privacy settings have been updated.',
      });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: (error) => {
      toast({
        title: 'Error saving profile',
        description: error instanceof Error ? error.message : 'Failed to save profile',
        variant: 'destructive',
      });
    },
  });

  const handleFormSubmit = (formData: EnhancedPersonalInfoFormValues, privacySettings: Record<string, boolean>) => {
    saveProfileMutation.mutate({ formData, privacySettings });
  };

  if (userLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!userLoading && !user) {
    return <Navigate to="/auth" replace />;
  }

  // Prepare default values from existing profile data
  const defaultValues = profileData ? {
    fullName: profileData.full_name || '',
    gender: profileData.gender || 'male',
    dateOfBirth: profileData.date_of_birth || '',
    heightCm: profileData.height_cm?.toString() || '',
    weightKg: profileData.weight_kg?.toString() || '',
    displayEmail: profileData.display_email || '',
    residenceCity: profileData.residence_city || '',
    residenceCountry: profileData.residence_country || '',
    religion: profileData.religion || '',
    casteOrSect: profileData.caste_or_sect || '',
    motherTongue: profileData.mother_tongue || '',
    aboutMe: profileData.about_me || '',
  } : undefined;

  // Prepare privacy settings from existing data
  const defaultPrivacySettings = profileData?.biodata_private_fields || {};

  return (
    <PageTransition>
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main className="flex-grow pt-28 pb-20">
          <div className="container px-6 mx-auto">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="mb-8">
                  <h1 className="text-3xl font-bold mb-2">Create Your Profile</h1>
                  <p className="text-muted-foreground">
                    Fill in your details and use the privacy toggles next to each field to control what information is public or private
                  </p>
                </div>

                <Card className="mb-6 bg-blue-50 border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg text-blue-800">🔒 Privacy-First Design</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-blue-700">
                      Each field has a privacy toggle. Information marked as <strong>"Private"</strong> will only be visible 
                      to users you specifically grant access to. <strong>"Public"</strong> information can be seen by anyone 
                      viewing your profile.
                    </p>
                  </CardContent>
                </Card>
                
                <EnhancedPersonalInfoForm
                  defaultValues={defaultValues}
                  defaultPrivacySettings={defaultPrivacySettings}
                  onSubmit={handleFormSubmit}
                  isSubmitting={saveProfileMutation.isPending}
                />

                {profileData && (
                  <Card className="mt-6 bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-medium text-green-800">Profile Status</h3>
                          <p className="text-sm text-green-600">
                            Profile created successfully. {profileData.status === 'active' ? 'Your profile is live!' : 'Complete payment to make your profile public.'}
                          </p>
                        </div>
                        {profileData.status === 'draft' && (
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground mb-2">Next step:</p>
                            <button
                              onClick={() => navigate('/preview')}
                              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                            >
                              Preview & Activate
                            </button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </motion.div>
            </div>
          </div>
        </main>
        
        <Footer />
      </div>
    </PageTransition>
  );
};

export default Profile;
